import { useEffect, useId, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/app/AuthContext.jsx';
import {
  useAcceptQuote,
  useBankAccounts,
  useCan,
  useQuoteDetail,
  useRejectQuote,
} from '@/hooks';
import {
  CHILEAN_BANKS,
  errorMessage,
  formatCLP,
  formatDeadlineUrgencyBadge,
} from '@/lib';
import { useQueryClient } from '@tanstack/react-query';
import { qk } from '@/lib/queryKeys';
import QuoteBankFields, { validateQuoteBank } from './QuoteBankFields.jsx';
import {
  chileYmd,
  formatDeadlineDate,
  isValidDateInput,
  MAX_DATE_INPUT,
  toISODate,
} from '@/lib/dateUtils';
import { apiErrorName, createBankAccount } from '@/lib/recepthor-api.js';
import { Button, Input, Spinner, useConfirm } from '@/ui';
import { ChevronLeftIcon } from '@/ui/icons';
import { mapQuoteToDetail, quotePriceLabel } from './mapQuote.js';
import PriceBreakdownFields from './PriceBreakdownFields.jsx';
import OfferBreakdownList from './OfferBreakdownList.jsx';
import {
  BREAKDOWN_STALE_ERRORS,
  breakdownFromItems,
  breakdownItems,
  offerBreakdownLines,
} from '@/lib/priceBreakdown.js';
import './SolicitudesInbox.css';
import './SolicitudDetail.css';

function Field({ label, value, wide = false }) {
  if (!value) return null;
  return (
    <div className={`sold-field${wide ? ' sold-field--wide' : ''}`}>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

// API refusals that mean the quote is no longer answerable here. The raw
// QuoteAlreadyAnswered message talks about an acceptance the receptor never
// made, and the status alone does not say who answered (the API also declines
// the quotes the firm did not pay), so the view says only what is left.
const ALREADY_ANSWERED = 'Esta solicitud ya no recibe tu cotización.';
const NOT_ACCEPTING = 'Esta solicitud ya no recibe ofertas.';
const QUOTE_SENT = 'Enviamos tu cotización al estudio.';
const NOT_CHOSEN = 'El estudio eligió otra cotización.';
const CANNOT_QUOTE = 'Ya no puedes cotizar esta solicitud.';
const REJECTED = 'Rechazaste esta solicitud.';
// Perfil calls the default account "principal".
const MISSING_DEFAULT = 'Para cotizar necesitas una cuenta principal.';
const DATE_BEFORE_TODAY = 'La fecha no puede ser anterior a hoy en Chile.';
const DATE_AFTER_DEADLINE =
  'La fecha debe estar dentro del plazo de la solicitud.';
// The same wording /oferta uses; the API's own text talks about a "tarea".
const ACTION_ERROR_TEXT = {
  DateOfferedAfterDeadline: DATE_AFTER_DEADLINE,
  DateOfferedBeforeToday: DATE_BEFORE_TODAY,
  PriceRequired: 'Indica un precio.',
  InvalidAmount: 'Indica un precio válido en pesos, entero y mayor que cero.',
};
// With a breakdown the API checks the sum against the price cap.
const BREAKDOWN_INVALID_AMOUNT = 'El total supera el monto máximo permitido.';
// The recipients changed after the receptor opened the request.
const BREAKDOWN_STALE =
  'Los destinatarios de la solicitud cambiaron. Revisa los montos y vuelve a cotizar.';

// 45000 reads as 45.000 once typed; parseQuotePrice takes both.
function formatPriceInput(raw) {
  const n = parseQuotePrice(raw);
  return n ? n.toLocaleString('es-CL') : raw;
}

function ClosedSummary({ children }) {
  return (
    <section className="sold-answer" aria-label="Estado de la solicitud">
      <p className="sold-answer__body">{children}</p>
    </section>
  );
}

ClosedSummary.propTypes = {
  children: PropTypes.node.isRequired,
};

function OfferedSummary({
  price,
  dateOffered,
  paidAt,
  note,
  breakdown,
  account,
}) {
  const amount = formatCLP(price);
  // dateOffered comes back as the chosen day at the end of the UTC day, the
  // same encoding as taskDeadline, so it reads with the calendar-day formatter.
  const day = formatDeadlineDate(dateOffered);
  const text = amount
    ? `Cotizaste ${amount}${day ? ` para el ${day}` : ''}.`
    : 'Cotizaste esta solicitud.';
  return (
    <section className="sold-answer" aria-label="Tu respuesta">
      <p className="sold-answer__body">{text}</p>
      <OfferBreakdownList lines={breakdown} />
      {account?.last4 ? (
        <p className="sold-answer__body">
          Te pagaremos en{' '}
          {CHILEAN_BANKS.find((bank) => bank.code === account.bankCode)?.name ??
            account.bankCode}{' '}
          terminada en {account.last4}.
        </p>
      ) : null}
      {note ? <p className="sold-answer__body">{note}</p> : null}
      {paidAt ? <p className="sold-answer__paid">Pagada</p> : null}
    </section>
  );
}

OfferedSummary.propTypes = {
  price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  dateOffered: PropTypes.string,
  paidAt: PropTypes.string,
  note: PropTypes.string,
  breakdown: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string, text: PropTypes.string }),
  ),
  account: PropTypes.shape({
    bankCode: PropTypes.string,
    accountType: PropTypes.string,
    last4: PropTypes.string,
  }),
};

export function parseQuotePrice(raw) {
  const value = String(raw ?? '').trim();
  if (!/^(?:[1-9]\d*|[1-9]\d{0,2}(?:\.\d{3})+)$/.test(value)) {
    return null;
  }
  const digits = value.replaceAll('.', '');
  const n = Number(digits);
  if (!Number.isSafeInteger(n) || n <= 0 || String(n) !== digits) {
    return null;
  }
  return n;
}

export default function SolicitudDetail() {
  const { quoteId } = useParams();
  // Back to the inbox view the quote was opened from.
  const [params] = useSearchParams();
  const answered = params.get('vista') === 'respondidas';
  const { organizationId } = useAuth();
  const canRead = useCan('bank:read');
  const canCreate = useCan('bank:create');
  const queryClient = useQueryClient();
  const accountsQuery = useBankAccounts(organizationId, {
    enabled: organizationId != null && canRead,
  });
  const accounts = Array.isArray(accountsQuery.data)
    ? accountsQuery.data
    : (accountsQuery.data?.results ?? []);
  const refreshAccounts = () =>
    queryClient.invalidateQueries({
      queryKey: qk.bankAccounts.list({ organizationId }),
    });
  const { data, error, isError, isPending } = useQuoteDetail(
    quoteId,
    organizationId,
  );
  const acceptMut = useAcceptQuote(organizationId);
  const rejectMut = useRejectQuote(organizationId);
  // The mutations invalidate only on success. A refusal because the quote was
  // already answered means the cached detail is stale: read it again.
  const refreshQuote = () =>
    queryClient.invalidateQueries({ queryKey: qk.quotes.all() });
  return (
    <SolicitudDetailView
      quoteId={quoteId}
      data={data}
      error={error}
      isError={isError}
      isPending={isPending}
      acceptMut={acceptMut}
      rejectMut={rejectMut}
      bankReadable={canRead}
      // The offer is paid to the default account and to no other: POST
      // /quotes/accept with a session takes it and refuses without one.
      bankAccount={accounts.find((account) => account.isDefault) ?? null}
      hasBankAccounts={canRead && accounts.length > 0}
      bankLoading={canRead && organizationId != null && accountsQuery.isPending}
      bankRefreshing={
        canRead && organizationId != null && accountsQuery.isFetching
      }
      canAddBank={
        canRead && canCreate && organizationId != null && !accountsQuery.isError
      }
      onCreateBank={async (body) => {
        await createBankAccount(body, { organizationId });
        await refreshAccounts();
      }}
      onPaymentRequired={refreshAccounts}
      onStale={refreshQuote}
      inboxPath={answered ? '/solicitudes?vista=respondidas' : '/solicitudes'}
    />
  );
}

export function SolicitudDetailView({
  quoteId,
  data,
  error,
  isError = false,
  isPending = false,
  acceptMut,
  rejectMut,
  inboxPath = '/solicitudes',
  bankAccount,
  hasBankAccounts = false,
  bankReadable = true,
  bankLoading = false,
  bankRefreshing = false,
  canAddBank = false,
  onCreateBank,
  onPaymentRequired,
  onStale,
}) {
  const [price, setPrice] = useState('');
  const [breakdown, setBreakdown] = useState({});
  const [breakdownErrors, setBreakdownErrors] = useState({});
  const formRef = useRef(null);
  const focusBreakdownError = useRef(false);
  // An amount that does not parse takes the focus, as /oferta does.
  useEffect(() => {
    if (!focusBreakdownError.current) return;
    focusBreakdownError.current = false;
    formRef.current
      ?.querySelector('.sold-breakdown [aria-invalid="true"]')
      ?.focus();
  }, [breakdownErrors]);
  const [dateOffered, setDateOffered] = useState('');
  const [formError, setFormError] = useState('');
  const [bankForm, setBankForm] = useState({
    bankCode: '',
    accountType: '',
    accountNumber: '',
    holderName: '',
    rut: '',
  });
  const [bankErrors, setBankErrors] = useState({});
  const [bankError, setBankError] = useState('');
  const [savingBank, setSavingBank] = useState(false);
  const savingBankRef = useRef(false);
  const [savedBank, setSavedBank] = useState(null);
  // The offer this view just sent. The detail stays on screen and shows it
  // read-only until the invalidated read returns the OFFERED quote.
  const [sentOffer, setSentOffer] = useState(null);
  // Same for a rejection: the detail says so until the read returns DECLINED.
  const [rejectedId, setRejectedId] = useState(null);
  const confirm = useConfirm();
  // The confirmation lives in ReceptorLayout, outside this keyed view, so it
  // can outlive it (browser back, a link reached with Tab). A confirm that
  // lands after this view left must not reject a quote no longer on screen.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const account = bankReadable ? (bankAccount ?? savedBank) : null;
  // With no account at all the form creates one, which becomes the default.
  // With accounts but none marked default, a new one would not be the default
  // either and the session accept takes no payment data: the default is
  // chosen in Perfil.
  const needsBank = canAddBank && !hasBankAccounts && !account && !bankLoading;
  const missingDefault =
    bankReadable && hasBankAccounts && !account && !bankLoading;
  const titleRef = useRef(null);
  const notesTitleId = useId();
  const formErrorId = useId();
  const breakdownId = useId();
  const bankErrorId = useId();
  const priceInvalid = formError === 'Indica un precio.';
  const dateInvalid =
    formError === 'Indica una fecha.' ||
    formError === 'Indica una fecha válida.';
  // A failed re-read keeps the last data (for example right after an offer),
  // so only a quote that never loaded shows the error view.
  const loadFailed = isError && !data;
  useEffect(() => {
    titleRef.current?.focus();
  }, [quoteId, data?.id, loadFailed, isPending]);

  const busy = savingBank || acceptMut.isPending || rejectMut.isPending;
  const actionError = acceptMut.error || rejectMut.error;
  const actionErrorName = apiErrorName(actionError?.body);
  const missingPaymentData = actionErrorName === 'PaymentDataRequired';
  const alreadyAnswered = actionErrorName === 'QuoteAlreadyAnswered';
  const notAccepting =
    apiErrorName(acceptMut.error?.body) === 'TaskNotAcceptingOffers';

  const view = data ? mapQuoteToDetail(data) : null;
  // The offered day can be neither before today in Chile nor after the
  // request's deadline (the API checks the same).
  const maxDate =
    view?.taskDeadline && !Number.isNaN(Date.parse(view.taskDeadline))
      ? toISODate(view.taskDeadline)
      : MAX_DATE_INPUT;
  const sent = sentOffer?.quoteId === quoteId ? sentOffer : null;
  const rejected = rejectedId === quoteId;
  const viewState = isPending || !view ? null : view.state;
  // A refused offer on a task that left the quoting cycle closes the view the
  // same way /oferta does; the next read cannot reopen it.
  const state =
    viewState === 'open' && sent
      ? 'offered'
      : viewState === 'open' && rejected
        ? 'declined'
        : viewState === 'open' && notAccepting
          ? 'closed'
          : viewState;
  const formOpen = state === 'open';
  // The form unmounts when an offer is sent or refused, or when a re-read shows
  // the quote answered. Focus would fall to the body, so it goes to the h1;
  // focus that was elsewhere (the inbox, another control) is left alone.
  const formWasOpen = useRef(false);
  useEffect(() => {
    const focusLost =
      !document.activeElement || document.activeElement === document.body;
    if (formWasOpen.current && !formOpen && focusLost) {
      titleRef.current?.focus();
    }
    formWasOpen.current = formOpen;
  }, [formOpen]);

  if (isPending) {
    // The view keeps its h1 while loading.
    return (
      <div className="sold sold--status sol-status">
        <h1 className="sol-status__body" ref={titleRef} tabIndex={-1}>
          Cargando solicitud…
        </h1>
        <Spinner aria-label="Cargando solicitud" />
        <p className="sol-status__body">
          Estamos buscando los datos y las notas del abogado para cotizar.
        </p>
      </div>
    );
  }

  if (loadFailed || !data) {
    const missing = error?.status === 404;
    return (
      <div className="sold sold--status">
        <Link to={inboxPath} className="sold-back">
          <ChevronLeftIcon size={16} />
          Solicitudes
        </Link>
        <h1 className="sol-status__body" ref={titleRef} tabIndex={-1}>
          {missing
            ? 'No encontramos esta solicitud.'
            : errorMessage(error, 'No pudimos cargar la solicitud.')}
        </h1>
        {missing ? (
          <p className="sol-status__body">
            Vuelve a Solicitudes para revisar las cotizaciones pendientes.
          </p>
        ) : null}
      </div>
    );
  }

  if (!view) {
    return (
      <div className="sold sold--status">
        <Link to={inboxPath} className="sold-back">
          <ChevronLeftIcon size={16} />
          Solicitudes
        </Link>
        <h1 className="sol-status__body" ref={titleRef} tabIndex={-1}>
          No pudimos cargar la solicitud.
        </h1>
      </div>
    );
  }
  const when = formatDeadlineDate(view.taskDeadline);
  // Only an open request has a deadline left to rush; the Respondidas inbox
  // drops the badge for the same reason.
  const urgency =
    state === 'open'
      ? formatDeadlineUrgencyBadge(
          view.taskDeadline,
          Date.now(),
          view.taskDeadlineExpiresAt,
        )
      : '';
  const notify = when ? `Notificar antes del ${when}` : '';
  const offer =
    view.state === 'offered'
      ? view
      : sent
        ? {
            price: sent.price,
            dateOffered: sent.dateOffered,
            paidAt: null,
            offerBreakdown: sent.offerBreakdown,
          }
        : null;
  const priceLabel =
    state === view.state
      ? view.priceLabel
      : quotePriceLabel(state, offer?.price);
  // The status region is mounted from the start, so each of these is announced
  // when it arrives.
  const notice = sent
    ? QUOTE_SENT
    : rejected
      ? REJECTED
      : alreadyAnswered
        ? ALREADY_ANSWERED
        : notAccepting
          ? NOT_ACCEPTING
          : '';

  const onAnswerError = (err) => {
    const name = apiErrorName(err?.body);
    // A breakdown the API refuses against the task's recipients means the
    // request changed: reading it again brings the current recipients.
    if (
      name === 'QuoteAlreadyAnswered' ||
      BREAKDOWN_STALE_ERRORS.includes(name)
    )
      onStale?.();
  };

  const onQuote = async (e) => {
    e.preventDefault();
    if (busy || bankLoading || bankRefreshing || savingBankRef.current) return;
    const rows = view.breakdownRows ?? [];
    const priced = rows.length ? breakdownItems(rows, breakdown) : null;
    if (priced) {
      focusBreakdownError.current = Object.keys(priced.errors).length > 0;
      setBreakdownErrors(priced.errors);
    }
    const parsed = priced ? priced.total : parseQuotePrice(price);
    const quoteError = !parsed
      ? priced
        ? 'Revisa los montos por destinatario.'
        : 'Indica un precio.'
      : !dateOffered
        ? 'Indica una fecha.'
        : !isValidDateInput(dateOffered)
          ? 'Indica una fecha válida.'
          : '';
    setFormError(quoteError);
    if (needsBank) {
      const { errors, body } = validateQuoteBank(bankForm);
      setBankErrors(errors);
      setBankError('');
      if (Object.keys(errors).length || quoteError) return;
      savingBankRef.current = true;
      setSavingBank(true);
      try {
        await onCreateBank(body);
        setSavedBank(body);
      } catch (err) {
        setBankError(
          errorMessage(err, 'No se pudo guardar. Intenta nuevamente.'),
        );
        return;
      } finally {
        savingBankRef.current = false;
        setSavingBank(false);
      }
    }
    if (quoteError) return;
    acceptMut.mutate(
      {
        quoteId,
        price: parsed,
        dateOffered,
        ...(priced ? { items: priced.items } : {}),
      },
      {
        onSuccess: () => {
          setSentOffer({
            quoteId,
            price: parsed,
            dateOffered: `${dateOffered}T23:59:59.999Z`,
            // Shown at once, before the re-read brings the stored breakdown.
            offerBreakdown: priced
              ? offerBreakdownLines(
                  breakdownFromItems(priced.items),
                  data?.recipients,
                )
              : [],
          });
        },
        onError: (err) => {
          if (apiErrorName(err?.body) === 'PaymentDataRequired') {
            setSavedBank(null);
            onPaymentRequired?.();
          }
          onAnswerError(err);
        },
      },
    );
  };

  const onReject = async () => {
    setFormError('');
    const ok = await confirm({
      title: '¿Rechazar esta solicitud?',
      body: 'El estudio sabrá que no la tomas y ya no podrás cotizarla.',
      confirmLabel: 'Rechazar',
      flat: true,
    });
    if (!ok || !mountedRef.current) return;
    rejectMut.mutate(
      { quoteId },
      { onSuccess: () => setRejectedId(quoteId), onError: onAnswerError },
    );
  };

  return (
    <div className="sold sold--receptor">
      <Link to={inboxPath} className="sold-back">
        <ChevronLeftIcon size={16} />
        Solicitudes
      </Link>

      <header className="sold-head">
        <p className="sold-meta">
          <span className="sold-rol">{view.rol}</span>
          {urgency ? <span className="sold-badge">{urgency}</span> : null}
        </p>
        <h1 className="sold-party" ref={titleRef} tabIndex={-1}>
          {view.party}
        </h1>
        {view.comuna ? <p className="sold-kind">{view.comuna}</p> : null}
        <p className="sold-price">{priceLabel}</p>
        {notify ? <p className="sold-due">{notify}</p> : null}
        <p className="sold-notice" role="status" aria-live="polite">
          {notice}
        </p>
      </header>

      {/* An answered request says so right under its price: on a phone the
          answer and its breakdown would otherwise sit under every field. On a
          wide screen it takes the side column, as the form does. */}
      {state === 'offered' ? (
        <OfferedSummary
          price={offer?.price}
          dateOffered={offer?.dateOffered}
          paidAt={offer?.paidAt}
          breakdown={offer?.offerBreakdown}
          account={offer?.payoutAccount}
        />
      ) : state === 'lost' ? (
        <OfferedSummary
          price={view.price}
          dateOffered={view.dateOffered}
          note={NOT_CHOSEN}
          breakdown={view.offerBreakdown}
        />
      ) : state === 'declined' ? (
        <ClosedSummary>{CANNOT_QUOTE}</ClosedSummary>
      ) : state === 'closed' ? (
        // After a refused offer the status region already says it.
        notAccepting ? null : (
          <ClosedSummary>{NOT_ACCEPTING}</ClosedSummary>
        )
      ) : null}

      <div className="sold-reading">
        {view.notes ? (
          <section className="sold-notes" aria-labelledby={notesTitleId}>
            <h2 id={notesTitleId} className="sold-notes__title">
              Notas del abogado
            </h2>
            <p className="sold-notes__body">{view.notes}</p>
          </section>
        ) : null}
        <dl className="sold-fields">
          <Field label="Actuación" value={view.actuacion} />
          <Field label="Procedimiento" value={view.procedure} />
          {(view.conservador ?? []).map(([label, value]) => (
            <Field key={label} label={label} value={value} />
          ))}
          <Field
            wide
            label={
              view.recipients?.length === 1 ? 'Destinatario' : 'Destinatarios'
            }
            value={
              view.recipients?.length ? (
                <ul className="sold-list" role="list">
                  {view.recipients.map((line, i) => (
                    <li key={i}>{line}</li>
                  ))}
                </ul>
              ) : null
            }
          />
          <Field label="Dirección" value={view.address} wide />
          <Field label="Tipo de domicilio" value={view.addressType} />
          <Field label="Comuna" value={view.comuna} />
          <Field label="Región" value={view.region} />
          <Field label="Tribunal" value={view.tribunalName} />
          <Field label="Estudio" value={view.firmName} />
        </dl>
      </div>

      {formOpen ? (
        // noValidate like /oferta: min and max guide the date picker, and an
        // out-of-range day comes back from the API with its translated text.
        <form
          ref={formRef}
          className="sold-form"
          aria-label="Cotización"
          noValidate
          onSubmit={onQuote}
        >
          {view.breakdownRows?.length ? (
            <PriceBreakdownFields
              idPrefix={breakdownId}
              rows={view.breakdownRows}
              values={breakdown}
              errors={breakdownErrors}
              onFormat={(index, key, value) =>
                setBreakdown((current) => ({
                  ...current,
                  [index]: { ...current[index], [key]: value },
                }))
              }
              onChange={(index, key, value) => {
                setBreakdown((current) => ({
                  ...current,
                  [index]: { ...current[index], [key]: value },
                }));
                setBreakdownErrors((current) =>
                  current[index]?.[key]
                    ? {
                        ...current,
                        [index]: { ...current[index], [key]: undefined },
                      }
                    : current,
                );
                if (formError) setFormError('');
              }}
              disabled={busy}
            />
          ) : (
            <label className="sold-control">
              <span>Precio</span>
              <Input
                variant="mono"
                inputMode="numeric"
                autoComplete="off"
                placeholder="45.000"
                value={price}
                onBlur={() => setPrice((current) => formatPriceInput(current))}
                aria-invalid={priceInvalid}
                aria-describedby={priceInvalid ? formErrorId : undefined}
                onChange={(e) => {
                  setPrice(e.target.value);
                  if (formError) setFormError('');
                }}
                disabled={busy}
              />
            </label>
          )}
          <label className="sold-control">
            <span>Fecha</span>
            <Input
              type="date"
              min={chileYmd(new Date())}
              max={maxDate}
              value={dateOffered}
              aria-invalid={dateInvalid}
              aria-describedby={dateInvalid ? formErrorId : undefined}
              onChange={(e) => {
                setDateOffered(e.target.value);
                if (formError) setFormError('');
              }}
              disabled={busy}
            />
          </label>
          {needsBank ? (
            <QuoteBankFields
              value={bankForm}
              errors={bankErrors}
              error={bankError}
              errorId={bankErrorId}
              disabled={busy}
              onChange={(next) => {
                setBankForm(next);
                setBankErrors({});
                setBankError('');
              }}
            />
          ) : bankLoading ? (
            <p className="sold-bank-summary">Cargando tu cuenta…</p>
          ) : missingDefault ? (
            <p className="sold-bank-summary">
              {MISSING_DEFAULT}{' '}
              <Link to="/perfil" className="sold-link">
                Márcala en Perfil
              </Link>
            </p>
          ) : account ? (
            <p className="sold-bank-summary">
              Te pagaremos en{' '}
              {CHILEAN_BANKS.find((bank) => bank.code === account.bankCode)
                ?.name ?? account.bankCode}{' '}
              terminada en {String(account.accountNumber ?? '').slice(-4)}.{' '}
              <Link to="/perfil" className="sold-link">
                Cambiar en Perfil
              </Link>
            </p>
          ) : null}
          {formError ? (
            <p className="sold-form-error" role="alert" id={formErrorId}>
              {formError}
            </p>
          ) : null}
          {missingPaymentData && hasBankAccounts ? (
            <p className="sold-form-error" role="alert">
              {MISSING_DEFAULT}{' '}
              <Link to="/perfil" className="sold-form-error__link">
                Márcala en Perfil
              </Link>{' '}
              y vuelve a esta solicitud para cotizar.
            </p>
          ) : missingPaymentData && canAddBank ? (
            <p className="sold-form-error" role="alert">
              Completa la cuenta donde recibirás el pago.
            </p>
          ) : missingPaymentData ? (
            <p className="sold-form-error" role="alert">
              Para cotizar necesitas cargar tu cuenta bancaria, donde recibes
              los pagos.{' '}
              <Link to="/perfil" className="sold-form-error__link">
                Cárgala en Perfil
              </Link>{' '}
              y vuelve a esta solicitud para cotizar.
            </p>
          ) : actionError && !alreadyAnswered ? (
            <p className="sold-form-error" role="alert">
              {BREAKDOWN_STALE_ERRORS.includes(actionErrorName)
                ? BREAKDOWN_STALE
                : actionErrorName === 'InvalidAmount' &&
                    view.breakdownRows?.length
                  ? BREAKDOWN_INVALID_AMOUNT
                  : (ACTION_ERROR_TEXT[actionErrorName] ??
                    errorMessage(
                      actionError,
                      'No pudimos enviar la cotización.',
                    ))}
            </p>
          ) : null}
          <div className="sold-actions">
            <Button
              variant="secondary"
              size="lg"
              disabled={busy}
              onClick={onReject}
            >
              Rechazar
            </Button>
            <Button
              variant="primary"
              size="lg"
              type="submit"
              disabled={busy || bankLoading || bankRefreshing}
            >
              Cotizar
            </Button>
          </div>
        </form>
      ) : null}
    </div>
  );
}

SolicitudDetailView.propTypes = {
  quoteId: PropTypes.string,
  data: PropTypes.object,
  error: PropTypes.object,
  isError: PropTypes.bool,
  isPending: PropTypes.bool,
  acceptMut: PropTypes.object.isRequired,
  rejectMut: PropTypes.object.isRequired,
  inboxPath: PropTypes.string,
  bankAccount: PropTypes.object,
  hasBankAccounts: PropTypes.bool,
  bankReadable: PropTypes.bool,
  bankLoading: PropTypes.bool,
  bankRefreshing: PropTypes.bool,
  canAddBank: PropTypes.bool,
  onCreateBank: PropTypes.func,
  onPaymentRequired: PropTypes.func,
  onStale: PropTypes.func,
};
